// useStandupShortcuts — Phase 6h: Standup-wide keyboard navigation.
//
// Intentionally separate from `useStatusShortcuts` (which handles in-thread
// R/A/O/M/Z bindings). This hook is wired at the Standup-page level and
// covers:
//   •  `?`       — open the shortcut cheat-sheet modal
//   •  `J`       — move focus to the next DecideCard in The Pile
//   •  `K`       — move focus to the previous DecideCard
//   •  `C`       — open the new-ticket composer (Polish #1)
//   •  `/`       — focus the global search input (dispatches a custom
//                  event; SearchBar will listen once Phase 6g lands)
//   •  `Escape`  — close the cheat sheet / clear any active focus ring
//
// The shortcuts are *ignored* while focus is inside an editable field
// (input / textarea / contenteditable) so Kimi can type freely in the
// composer without triggering navigation.
import { useEffect } from 'react';

export type StandupShortcutHandlers = {
  /** Focus next pile card (J). */
  onNext: () => void;
  /** Focus previous pile card (K). */
  onPrev: () => void;
  /** Open the cheat sheet (?). */
  onCheatSheet: () => void;
  /** Open the new-ticket composer (C). Polish #1. */
  onCompose: () => void;
  /** Close any open modal + clear focus (Escape). */
  onEscape: () => void;
};

function isEditableTarget(t: EventTarget | null): boolean {
  if (!(t instanceof HTMLElement)) return false;
  const tag = t.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (t.isContentEditable) return true;
  return false;
}

export function useStandupShortcuts(handlers: StandupShortcutHandlers): void {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (isEditableTarget(e.target)) return;
      // Ignore when a modifier (Ctrl/Cmd/Alt) is pressed — those are either
      // browser shortcuts or future compose-specific chords.
      if (e.ctrlKey || e.metaKey || e.altKey) return;

      if (e.key === '?' || (e.shiftKey && e.key === '/')) {
        e.preventDefault();
        handlers.onCheatSheet();
        return;
      }
      if (e.key === '/') {
        e.preventDefault();
        // Phase 6g will wire a search input; until then fire a custom event
        // so the future input can grab focus without needing to own this
        // hook.
        window.dispatchEvent(new CustomEvent('md-inbox:focus-search'));
        return;
      }
      if (e.key === 'j' || e.key === 'J') {
        e.preventDefault();
        handlers.onNext();
        return;
      }
      if (e.key === 'k' || e.key === 'K') {
        e.preventDefault();
        handlers.onPrev();
        return;
      }
      if (e.key === 'c' || e.key === 'C') {
        e.preventDefault();
        handlers.onCompose();
        return;
      }
      if (e.key === 'Escape') {
        handlers.onEscape();
        return;
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handlers]);
}

/**
 * Cycle focus among elements matching a CSS selector, with wrap-around.
 * Returns the new index. If nothing is currently focused, starts at 0.
 */
export function cycleFocus(selector: string, direction: 1 | -1): number {
  const nodes = Array.from(
    document.querySelectorAll<HTMLElement>(selector),
  ).filter((n) => n.offsetParent !== null); // skip hidden
  if (nodes.length === 0) return -1;
  const current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  let idx = current ? nodes.indexOf(current) : -1;
  if (idx === -1) {
    idx = direction === 1 ? 0 : nodes.length - 1;
  } else {
    idx = (idx + direction + nodes.length) % nodes.length;
  }
  nodes[idx]?.focus();
  nodes[idx]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  return idx;
}
